import { AgreementsService } from "@/services/agreements";
import { readAgreement } from "@/schemas/agreement";
import { Module, PATH_AGREEMENT } from "@/constants";
import ContractorAssignmentsList from "./ContractorAssignmentsList";
import { ContractorPlannedHoursList } from "./ContractorPlannedHoursList";
import { useContractorData } from "../hooks";
import { RoleBasedAssignmentsService } from "@/services/roleBasedAssignmentsService";
import { readRoleBasedAssignments } from "@/schemas/roleBasedAssignments";
import { DepartmentBasedAssignmentsService } from "@/services/departmentBasedAssignmentsService";
import { readDepartmentBasedAssignments } from "@/schemas/departmentBasedAssignments";
import { generatePath } from "react-router-dom";
import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { ReadAgreement } from "@/types/agreement";
import { ReadRoleBasedAssignments } from "@/types/roleBasedAssignments";
import { ReadDepartmentBasedAssignments } from "@/types/departmentBasedAssignments";
import { AgreementStatusBadge } from "@/modules/agreement/components/AgreementStatusBadge";
import TaxStatus from "@/components/TaxStatus";
import { useTranslation } from "react-i18next";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

const ContractorAssignments = () => {
  const contractor = useContractorData();
  const { t } = useTranslation();
  const { contractors_planned_hours_read } = useModulePermissions(
    Module.Contractors,
  );

  const agreementsColumnOverrides = useMemo(
    (): Record<string, ColumnDef<ReadAgreement>> => ({
      agreement_status: {
        accessorKey: "agreement_status" as const,
        cell: ({ row }: { row: { original: ReadAgreement } }) => (
          <div className="flex items-center h-full">
            <AgreementStatusBadge
              className="text-xs"
              status={row.original.agreement_status}
            />
          </div>
        ),
      },
      agreement_tax_status: {
        accessorKey: "agreement_tax_status" as const,
        cell: ({ row }: { row: { original: ReadAgreement } }) => (
          <div className="flex items-center h-full">
            <TaxStatus
              className="text-xs"
              value={row.original.agreement_tax_status ?? undefined}
            />
          </div>
        ),
      },
    }),
    [],
  );

  const roleBasedColumnOverrides = useMemo(
    (): Record<string, ColumnDef<ReadRoleBasedAssignments>> => ({
      status: {
        accessorKey: "status" as const,
        cell: ({ row }: { row: { original: ReadRoleBasedAssignments } }) => (
          <span className="flex items-center h-full">
            {row.original.status
              ? t(`statuses.${String(row.original.status)}`)
              : null}
          </span>
        ),
      },
    }),
    [t],
  );

  const departmentBasedColumnOverrides = useMemo(
    (): Record<string, ColumnDef<ReadDepartmentBasedAssignments>> => ({
      status: {
        accessorKey: "status" as const,
        cell: ({
          row,
        }: {
          row: { original: ReadDepartmentBasedAssignments };
        }) => (
          <span className="flex items-center h-full">
            {row.original.status
              ? t(`statuses.${String(row.original.status)}`)
              : null}
          </span>
        ),
      },
    }),
    [t],
  );

  return (
    <>
      {contractors_planned_hours_read && (
        <ContractorPlannedHoursList contractorId={contractor.id} />
      )}
      <ContractorAssignmentsList
        contractorId={contractor.id}
        name="agreements"
        service={AgreementsService}
        schema={readAgreement}
        titleKey="contractors.assignments.agreements.title"
        translationKey="agreements"
        pathBuilder={(row) =>
          generatePath(PATH_AGREEMENT, {
            agreementId: row.id,
          })
        }
        overrideFilter={{
          key: "contractor_user_ref_number",
          value: contractor.user_ref_number,
        }}
        columnOverrides={agreementsColumnOverrides}
      />
      <ContractorAssignmentsList
        contractorId={contractor.id}
        name="roleBasedAssignments"
        service={RoleBasedAssignmentsService}
        schema={readRoleBasedAssignments}
        titleKey="contractors.assignments.roleBased.title"
        translationKey="roleBasedAssignments"
        columnOverrides={roleBasedColumnOverrides}
      />
      <ContractorAssignmentsList
        contractorId={contractor.id}
        name="departmentBasedAssignments"
        service={DepartmentBasedAssignmentsService}
        schema={readDepartmentBasedAssignments}
        titleKey="contractors.assignments.departmentBased.title"
        translationKey="departmentBasedAssignments"
        columnOverrides={departmentBasedColumnOverrides}
      />
    </>
  );
};

export default ContractorAssignments;
